import React, { useState, useEffect } from "react";
import { Grid, Radio, notification } from "antd";
import type { RadioChangeEvent } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { subDays } from "date-fns";

import { Button, Box, Input, DateRangePicker } from "components";
import { CloseIcon, SendCompleteIcon, ConfirmFailIcon } from "icons";
import {
  localStorageHelper,
  handleConvertDateYear,
  handleConvertMonthYear,
} from "utils";

import { NewStressCheckData } from "models/stressCheck/newStressCheck";
import { CreateStressCheckActionCreators } from "redux/rootActions";
import {
  selectIsSaved,
  NewStressCheckDataResponse,
  selectSaveErrorMessage,
  selectIsSavingStressCheck,
} from "redux/stressCheck/newStressCheck/newStressCheckStates";

import {
  ItemsContainer,
  LeftItem,
  RightItem,
  TextTitle,
  AudioWrapper,
  DateRangeItem,
  ButtonsContainer,
  CreateTitleContainer,
} from "./Stress.style";
import ConfirmModal from "screens/changeStudentAffiliation/ConfirmModal";

const { useBreakpoint } = Grid;

interface INewStressCheck {
  handleCancel: () => void;
}

const NewStressCheck: React.FC<INewStressCheck> = ({ handleCancel }) => {
  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const { t }: any = useTranslation("translation", {
    keyPrefix: "stress-check",
  });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "mental-health",
  });

  const screens = useBreakpoint();
  const dispatch = useDispatch();

  const NewStressCheckSaved = useSelector(selectIsSaved);
  const savingNewStressCheck = useSelector(selectIsSavingStressCheck);
  const NewStressCheckResponse = useSelector(NewStressCheckDataResponse);
  const NewStressCheckError = useSelector(selectSaveErrorMessage);
  const [examinationPassed, setExaminationPassed] = useState<boolean>(false);
  const [radioValue, setRadioValue] = useState<number>(1);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(moment().add(1, "M").toDate());
  const [testName, setTestName] = useState<string>(
    `${handleConvertMonthYear(
      moment(startDate).format("YYYY-MM-DD"),
      currentLanguage
    )}${t("stress-check-placeholder")}`
  );
  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
  const [isVisibleModalLeave, setIsVisibleModalLeave] =
    useState<boolean>(false);
  const [error, setError] = useState({
    status: "",
    error: "",
  });
  const [inProgress, setInProgress] = useState<boolean>(true);

  const onChangeRadio = (e: RadioChangeEvent) => {
    setRadioValue(e.target.value);
  };

  const onChangeTestName = (e: any) => {
    setTestName(e.target.value);
    if (error.status === "test-name") {
      setError({
        status: "",
        error: "",
      });
    }
  };

  const onCreateTest = () => {
    const newStressCheckParams: NewStressCheckData = {
      stress_check_type_id: radioValue,
      name: testName,
      start_time: moment(startDate).toString(),
      end_time: moment(endDate).toString(),
      is_temp_save: "false",
    };
    dispatch(
      CreateStressCheckActionCreators.newStressCheckAction(newStressCheckParams)
    );
  };

  const handleOpenModal = () => {
    if (!testName) {
      setError({
        status: "test-name",
        error: "enter-test-name",
      });
      return;
    }
    if (examinationPassed) {
      setError({
        status: "passed",
        error: "exam-period-passed",
      });
      return;
    }
    if (moment(startDate).diff(moment(new Date())) > 0) {
      setInProgress(false);
    } else {
      setInProgress(true);
    }
    setIsVisibleModal(true);
  };

  const handleConfirm = () => {
    onCreateTest();
    setIsVisibleModal(false);
  };

  const handleSaveDraft = () => {
    if (!testName) {
      setError({
        status: "test-name",
        error: "enter-test-name",
      });
      return;
    }
    if (examinationPassed) {
      setError({
        status: "passed",
        error: "exam-period-passed",
      });
      return;
    }

    const newStressCheckParams: NewStressCheckData = {
      stress_check_type_id: radioValue,
      name: testName,
      start_time: moment(startDate).toString(),
      end_time: moment(endDate).toString(),
      is_temp_save: "true",
    };
    dispatch(
      CreateStressCheckActionCreators.newStressCheckAction(newStressCheckParams)
    );
  };

  useEffect(() => {
    if (moment(endDate).diff(moment(subDays(new Date(), 1))) < 0) {
      setExaminationPassed(true);
    } else {
      setExaminationPassed(false);
      setError({
        status: "",
        error: "",
      });
    }
  }, [endDate]);

  useEffect(() => {
    if (NewStressCheckSaved) {
      notification.success({
        placement: "bottomRight",
        message: t1("created"),
        icon: <SendCompleteIcon />,
      });
      dispatch(CreateStressCheckActionCreators.handleResetDataAction());
      handleCancel();
    }
  }, [NewStressCheckSaved]);

  useEffect(() => {
    if (NewStressCheckError) {
      notification.error({
        placement: "bottomRight",
        message: NewStressCheckError,
        icon: <ConfirmFailIcon width={53} height={53} />,
      });
      dispatch(CreateStressCheckActionCreators.handleResetDataAction());
    }
  }, [NewStressCheckError]);

  useEffect(() => {
    dispatch(CreateStressCheckActionCreators.handleResetDataAction());
    setError({
      status: "",
      error: "",
    });
    setIsVisibleModal(false);
  }, []);

  return (
    <Box title={t("stress-check-management")} padding="16px 16px 32px">
      <CreateTitleContainer
        style={{
          paddingBottom: 24,
          marginTop: 8,
          marginBottom: 0,
          borderBottom: "1px solid #F2F2F2",
        }}
      >
        <TextTitle>{t("new-stress-check")}</TextTitle>
        <CloseIcon
          fill="#3B3B3B"
          style={{
            marginRight: 8,
            cursor: "pointer",
          }}
          onClick={() => setIsVisibleModalLeave(true)}
        />
      </CreateTitleContainer>
      <ItemsContainer
        style={{
          paddingTop: 22,
          paddingBottom: 22,
        }}
        direction="row"
      >
        <LeftItem>{t1("action-item-number")}</LeftItem>
        <Radio.Group onChange={onChangeRadio} value={radioValue}>
          <AudioWrapper>
            <Radio value={1}>{t("25-questions")}</Radio>
            <Radio value={2}>{t("59-questions")}</Radio>
          </AudioWrapper>
        </Radio.Group>
      </ItemsContainer>
      <ItemsContainer>
        <LeftItem>{t1("test-name")}</LeftItem>
        <RightItem>
          <Input
            height={40}
            value={testName}
            onChange={onChangeTestName}
            placeholder={t1("test-name-placeholder")}
          />
          {error.status === "test-name" && error.error && (
            <div className="red-text">{t1(error.error)}</div>
          )}
        </RightItem>
      </ItemsContainer>
      <ItemsContainer>
        <LeftItem>{t1("examination-period")}</LeftItem>
        <DateRangeItem>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            locale={currentLanguage}
            maxDate={false}
            minDate={true}
          />
          {error.status === "passed" && error.error && (
            <div className="red-text">{t1(error.error)}</div>
          )}
        </DateRangeItem>
      </ItemsContainer>
      <ButtonsContainer>
        <Button
          color="#FFFFFF"
          fontSize={16}
          fontWeight={700}
          lineHeight="16px"
          background={"#3B3B3B"}
          padding="12px 24px 12px"
          name={t1("save-as-draft")}
          bdr="6px"
          border="none"
          onClick={handleSaveDraft}
          disabled={!testName || savingNewStressCheck}
        />
        <Button
          color="#FFFFFF"
          fontSize={16}
          fontWeight={700}
          lineHeight="16px"
          background={"#2AC769"}
          padding="12px 24px 12px"
          name={t("create-new")}
          bdr="6px"
          border="none"
          onClick={handleOpenModal}
          disabled={!testName || savingNewStressCheck}
        />
      </ButtonsContainer>
      {isVisibleModal && (
        <ConfirmModal
          isVisible={isVisibleModal}
          handleCancel={() => setIsVisibleModal(false)}
          handleOk={handleConfirm}
        >
          <div>{t("notification-will-be-sent")}</div>
          <div>{t("are-you-sure")}</div>
        </ConfirmModal>
      )}
      {isVisibleModalLeave && (
        <ConfirmModal
          isVisible={isVisibleModalLeave}
          handleCancel={() => setIsVisibleModalLeave(false)}
          handleOk={handleCancel}
        >
          <div
            style={{
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "20px",
            }}
          >
            {t1("confirm-leaving")}
          </div>
        </ConfirmModal>
      )}
    </Box>
  );
};

export default NewStressCheck;
